import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { api } from '../utils/api';
import { 
  LuBell, LuCircleAlert, LuInfo, LuX, LuPlus, 
  LuCircleCheck, LuEye, LuEyeOff, LuTrash2, LuSettings
} from 'react-icons/lu';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog';
import { getAlertObservation } from '../utils/alertObservation';

const CONDITION_SCHEMA = {
  cpu_high: { label: 'CPU Usage High', unit: '%', thresholdLabel: 'CPU threshold (%)', min: 0, max: 100, defaultValue: 80 },
  memory_high: { label: 'Memory Usage High', unit: '%', thresholdLabel: 'Memory threshold (%)', min: 0, max: 100, defaultValue: 80 },
  cost_spike: { label: 'Monthly Cost Above', unit: 'USD', thresholdLabel: 'Monthly cost threshold (USD)', min: 0, max: 10000000, defaultValue: 100 },
  status_stopped: { label: 'Resource Stopped', unit: null, thresholdLabel: null, min: 0, max: 0, defaultValue: 0 },
};

const Alerts = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [alertRules, setAlertRules] = useState([]);
  const [showCreateRule, setShowCreateRule] = useState(false);
  const [alertsError, setAlertsError] = useState(null);
  const [rulesError, setRulesError] = useState(null);
  const [resources, setResources] = useState([]);
  const [health, setHealth] = useState(null);
  const [resourcesError, setResourcesError] = useState(null);
  const [healthError, setHealthError] = useState(null);
  const [newRule, setNewRule] = useState({
    name: '',
    condition_type: 'cpu_high',
    threshold: 80,
    enabled: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true); setAlertsError(null); setRulesError(null); setResourcesError(null); setHealthError(null);
    const [alertsResult, rulesResult, resourcesResult, healthResult] = await Promise.allSettled([
      api.getAlerts(), api.getAlertRules(), api.getResources(), api.getHealthSummary(),
    ]);
    if (alertsResult.status === 'fulfilled') setAlerts(alertsResult.value.data || []);
    else setAlertsError('Active alerts could not be loaded. No healthy conclusion is being shown.');
    if (rulesResult.status === 'fulfilled') setAlertRules(rulesResult.value.data || []);
    else setRulesError('Alert rules could not be loaded. Existing rules may still be active.');
    if (resourcesResult.status === 'fulfilled') setResources(resourcesResult.value.data || []);
    else setResourcesError('Inventory state could not be loaded.');
    if (healthResult.status === 'fulfilled') setHealth(healthResult.value.data || {});
    else setHealthError('System health could not be loaded.');
    setLoading(false);
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();
    try {
      await api.createAlertRule(newRule);
      toast.success('Alert rule created');
      setShowCreateRule(false);
      setNewRule({ name: '', condition_type: 'cpu_high', threshold: 80, enabled: true });
      loadData();
    } catch (error) {
      toast.error('Failed to create alert rule');
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await api.updateAlertStatus(alertId, 'acknowledged');
      toast.success('Alert acknowledged');
      loadData();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleResolve = async (alertId) => {
    try {
      await api.updateAlertStatus(alertId, 'resolved');
      toast.success('Alert resolved');
      loadData();
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Delete this alert rule?')) return;
    try {
      await api.deleteAlertRule(ruleId);
      toast.success('Alert rule deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete alert rule');
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      info: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
      warning: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
      critical: 'text-rose-400 bg-rose-500/10 border-rose-500/30'
    };
    return colors[severity] || colors.info;
  };

  const getSeverityIcon = (severity) => {
    const icons = {
      info: LuInfo,
      warning: LuCircleAlert,
      critical: LuCircleAlert
    };
    const Icon = icons[severity] || LuInfo;
    return <Icon className="w-5 h-5" />;
  };

  const getConditionTypeLabel = (type) => {
    return CONDITION_SCHEMA[type]?.label || type;
  };

  const conditionSummary = (rule) => {
    const schema = CONDITION_SCHEMA[rule.condition_type];
    if (!schema) return rule.condition_type;
    if (!schema.unit) return schema.label;
    return `${schema.label} > ${schema.unit === 'USD' ? '$' : ''}${rule.threshold}${schema.unit === '%' ? '%' : ''}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-zinc-700 border-t-amber-400 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-500 text-sm">Loading alerts...</p>
          </div>
        </div>
      </div>
    );
  }

  const openAlerts = alerts.filter(a => a.status === 'open');
  const acknowledgedAlerts = alerts.filter(a => a.status === 'acknowledged');
  const resolvedAlerts = alerts.filter(a => a.status === 'resolved');
  const observation = getAlertObservation({
    alerts, rules: alertRules, resources, health,
    alertsError: Boolean(alertsError), rulesError: Boolean(rulesError),
    resourcesError: Boolean(resourcesError), healthError: Boolean(healthError),
  });
  const observationStyles = {
    danger: 'border-red-500/25 bg-red-500/10 text-red-100',
    warning: 'border-amber-500/25 bg-amber-500/10 text-amber-100',
    neutral: 'border-zinc-700 bg-zinc-900/70 text-zinc-200',
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-zinc-100">Alerts</h1>
            <p className="text-zinc-500 mt-1">Monitor and manage system alerts</p>
          </div>
          <button
            onClick={() => setShowCreateRule(true)}
            disabled={Boolean(rulesError)}
            title={rulesError ? 'Alert rules must load before a new rule can be created' : undefined}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-zinc-900 rounded-lg font-medium flex items-center gap-2 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          >
            <LuPlus className="w-4 h-4" />
            New Alert Rule
          </button>
        </div>

        <div className={`mb-8 rounded-xl border p-5 ${observationStyles[observation.tone]}`} role="status">
          <p className="font-medium">{observation.title}</p>
          <p className="mt-1 text-sm opacity-80">{observation.detail}</p>
          {(alertsError || rulesError || resourcesError || healthError) && (
            <button onClick={loadData} className="mt-3 min-h-10 rounded-lg border border-current/20 px-3 text-sm font-medium">Retry unavailable data</button>
          )}
        </div>
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-rose-500/10 flex items-center justify-center">
                <LuCircleAlert className="w-5 h-5 text-rose-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-zinc-100">{alertsError ? '—' : openAlerts.length}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Open Alerts</p>
              </div>
            </div>
          </div>
          
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <LuEye className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-zinc-100">{alertsError ? '—' : acknowledgedAlerts.length}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Acknowledged</p>
              </div>
            </div>
          </div>
          
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center">
                <LuSettings className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-zinc-100">{rulesError ? '—' : alertRules.length}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Alert Rules</p>
              </div>
            </div>
          </div>
        </div>

        {/* Alert Rules */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">Alert Rules</h2>
          {rulesError ? <div className="glass-card rounded-xl p-6 text-sm text-zinc-400">Rule inventory is unavailable.</div> : alertRules.length === 0 ? (
            <div className="glass-card rounded-xl p-8 text-center">
              <LuSettings className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No alert rules configured</p>
              <button
                onClick={() => setShowCreateRule(true)}
                className="mt-4 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg text-sm transition-colors"
              >
                Create your first rule
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {alertRules.map((rule) => (
                <div key={rule.id} className="glass-card rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-2 h-2 rounded-full ${rule.enabled ? 'bg-teal-400' : 'bg-zinc-600'}`} />
                    <div>
                      <p className="text-sm font-medium text-zinc-200">{rule.name}</p>
                      <p className="text-xs text-zinc-500">
                        {conditionSummary(rule)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteRule(rule.id)}
                    className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                  >
                    <LuTrash2 className="w-4 h-4 text-zinc-500" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active Alerts */}
        <div>
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">Active Alerts</h2>
          {alertsError ? <div className="glass-card rounded-xl p-6 text-sm text-zinc-400">Alert state is unavailable; Cloud Watcher cannot confirm that systems are healthy.</div> : openAlerts.length === 0 && acknowledgedAlerts.length === 0 ? (
            <div className="glass-card rounded-xl p-8 text-center">
              <LuInfo className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
              <p className="text-zinc-400">No active alerts observed</p>
              <p className="text-sm text-zinc-600 mt-1">See the assessment above for monitoring coverage and inventory health.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {[...openAlerts, ...acknowledgedAlerts].map((alert) => (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`glass-card rounded-lg p-4 border ${getSeverityColor(alert.severity)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      {getSeverityIcon(alert.severity)}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-zinc-200">{alert.message}</p>
                        <p className="text-xs text-zinc-500 mt-1">
                          {new Date(alert.created_at).toLocaleString()}
                        </p>
                        {alert.status === 'acknowledged' && (
                          <span className="inline-block mt-2 px-2 py-1 bg-amber-500/10 text-amber-400 text-xs rounded">
                            Acknowledged
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {alert.status === 'open' && (
                        <button
                          onClick={() => handleAcknowledge(alert.id)}
                          className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                          title="Acknowledge"
                        >
                          <LuEye className="w-4 h-4 text-zinc-400" />
                        </button>
                      )}
                      <button
                        onClick={() => handleResolve(alert.id)}
                        className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                        title="Resolve"
                      >
                        <LuCircleCheck className="w-4 h-4 text-zinc-400" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        <Dialog open={showCreateRule} onOpenChange={setShowCreateRule}>
          {showCreateRule && <DialogContent className="border-zinc-700 bg-zinc-900 text-zinc-100">
              <DialogTitle>Create Alert Rule</DialogTitle>
              <DialogDescription className="text-zinc-400">Choose a condition and an explicit threshold. Existing rules remain unchanged until you submit.</DialogDescription>
              <form onSubmit={handleCreateRule} className="space-y-4">
                <div>
                  <label htmlFor="alert-rule-name" className="block text-sm font-medium text-zinc-300 mb-2">
                    Rule Name
                  </label>
                  <input
                    id="alert-rule-name"
                    type="text"
                    value={newRule.name}
                    onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                    className="w-full px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 focus:outline-none focus:border-amber-500"
                    placeholder="High CPU Alert"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="alert-condition" className="block text-sm font-medium text-zinc-300 mb-2">
                    Condition Type
                  </label>
                  <select
                    id="alert-condition"
                    value={newRule.condition_type}
                    onChange={(e) => setNewRule({ ...newRule, condition_type: e.target.value, threshold: CONDITION_SCHEMA[e.target.value].defaultValue })}
                    className="w-full px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 focus:outline-none focus:border-amber-500"
                  >
                    <option value="cpu_high">CPU Usage High</option>
                    <option value="memory_high">Memory Usage High</option>
                    <option value="cost_spike">Cost Spike</option>
                    <option value="status_stopped">Resource Stopped</option>
                  </select>
                </div>
                
                {CONDITION_SCHEMA[newRule.condition_type].thresholdLabel && <div>
                  <label htmlFor="alert-threshold" className="block text-sm font-medium text-zinc-300 mb-2">
                    {CONDITION_SCHEMA[newRule.condition_type].thresholdLabel}
                  </label>
                  <input
                    id="alert-threshold"
                    type="number"
                    value={newRule.threshold}
                    onChange={(e) => setNewRule({ ...newRule, threshold: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 focus:outline-none focus:border-amber-500"
                    min={CONDITION_SCHEMA[newRule.condition_type].min}
                    max={CONDITION_SCHEMA[newRule.condition_type].max}
                    required
                  />
                </div>}
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="enabled"
                    checked={newRule.enabled}
                    onChange={(e) => setNewRule({ ...newRule, enabled: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="enabled" className="text-sm text-zinc-300">
                    Enable rule immediately
                  </label>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateRule(false)}
                    className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-zinc-900 rounded-lg font-medium transition-colors"
                  >
                    Create Rule
                  </button>
                </div>
              </form>
          </DialogContent>}
        </Dialog>
      </div>
    </div>
  );
};

export default Alerts;
